import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, Divider, Button } from "@mui/material";

import TypeSelector from "./fields/TypeSelector";
import ThresholdFields from "./fields/ThresholdFields";
import BuyXGetYFields from "./fields/BuyXGetYFields";
import ProductDiscountFields from "./fields/ProductDiscountFields";
import CategoryDiscountFields from "./fields/CategoryDiscountFields";
import SubcategoryDiscountFields from "./fields/SubcategoryDiscountFields";
import StackingConfigFields from "./fields/StackingConfigFields";
import TogglesAndMeta from "./fields/TogglesAndMeta";
import StartEndDateFields from "./fields/StartEndDateFields";

const defaultState = {
  name: "",
  type: "product_discount",
  enabled: true,
  autoApply: true,
  couponCode: "",
  config: {},
  conditions: {},
  stacking: {},
  appliesTo: {},
};

const DiscountFormModal = ({ open, onClose, onSave, initialData }) => {
  const [form, setForm] = useState(defaultState);
  const [nameTouched, setNameTouched] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
      setNameTouched(true); // preserve existing name
    } else {
      setForm(defaultState);
      setNameTouched(false);
    }
  }, [initialData]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: value },
    }));
  };

  const handleSubmit = () => {
    onSave(form);
  };

  // 🔁 Auto-naming logic
  useEffect(() => {
    if (nameTouched) return;

    const { type, config, appliesTo } = form;
    const val = config.discountValue || "";
    const unit = config.discountType === "fixed" ? "$" : "%";
    let name = "";

    if (type === "product_discount") {
      const target = appliesTo?.productTargets?.[0];
      if (val && target?.productType) {
        name = `${val}${unit} Off ${target.productType}`;
      }
    }

    if (type === "category_discount") {
      const targets = appliesTo?.collectionTypes?.join(", ");
      if (val && targets) {
        name = `${val}${unit} Off ${targets}`;
      }
    }

    if (type === "subcategory_discount") {
      const subs = appliesTo?.subcategories?.join(", ");
      if (val && subs) {
        name = `${val}${unit} Off ${subs}`;
      }
    }

    if (type === "threshold_discount") {
      if (val && config.thresholdAmount) {
        name = `${val}${unit} Off Orders Over $${config.thresholdAmount}`;
      }
    }

    if (name) {
      setForm((prev) => ({ ...prev, name: name.toUpperCase() }));
    }
  }, [form.type, form.config, form.appliesTo, nameTouched]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "white",
          borderRadius: 2,
          width: 600,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}>
        <Box sx={{ overflowY: "auto", p: 4, flexGrow: 1 }}>
          <Typography variant="h6" mb={2}>
            {form._id ? "Edit Discount Rule" : "Create Discount Rule"}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.5 }}>
              Discount Name
            </Typography>
            <input
              type="text"
              value={form.name}
              onChange={(e) => {
                setNameTouched(true);
                handleChange("name", e.target.value.toUpperCase());
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ccc",
                borderRadius: 6,
                fontSize: 14,
                fontFamily: "inherit",
              }}
              placeholder="Enter discount name or leave blank to auto-generate"
            />
          </Box>

          <TypeSelector form={form} handleChange={handleChange} />

          {form.type === "threshold_discount" && (
            <ThresholdFields
              form={form}
              handleNestedChange={handleNestedChange}
            />
          )}

          {form.type === "buy_x_get_y" && (
            <BuyXGetYFields form={form} setForm={setForm} />
          )}

          {form.type === "product_discount" && (
            <ProductDiscountFields
              form={form}
              handleNestedChange={handleNestedChange}
              setForm={setForm}
            />
          )}

          {form.type === "category_discount" && (
            <CategoryDiscountFields
              form={form}
              handleNestedChange={handleNestedChange}
              setForm={setForm}
            />
          )}

          {form.type === "subcategory_discount" && (
            <SubcategoryDiscountFields
              form={form}
              handleNestedChange={handleNestedChange}
              setForm={setForm}
            />
          )}

          <StartEndDateFields form={form} setForm={setForm} />

          <StackingConfigFields form={form} setForm={setForm} />
          <TogglesAndMeta form={form} handleChange={handleChange} />

          <Box sx={{ height: 10 }} />
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #eee",
            backgroundColor: "white",
            display: "flex",
            justifyContent: "space-between",
            zIndex: 2,
          }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DiscountFormModal;
